<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('users::page.title.users') }}</h2>

            <div>
                <b-datatable
                    :key="datatableKey"
                    :columns="columns"
                    :endpoint="listRoute"
                    :hidden-columns="hiddenColumns"
                    :initial-filters="initialFilters"
                    has-filter-selector
                    item-key="id"
                >
                    <template #buttons>
                        <div class="px-2">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        v-if="can('api.v1.users.store') && can('users.create')"
                                        color="primary"
                                        icon
                                        v-bind="attrs"
                                        :href="createItemUrl()"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-plus-circle</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.create.new') }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                    <template #filters="{ filters }">
                        <v-col cols="4" class="pb-0">
                            <b-filter-input
                                v-model="filters.filters.name"
                                :label="t('users::label.name')"
                                class="flex-grow-0"
                            ></b-filter-input>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-input
                                v-model="filters.filters.email"
                                :label="t('users::label.email')"
                                class="flex-grow-0"
                            ></b-filter-input>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-input-roles
                                v-model="filters.filters.roles"
                                :label="t('users::label.role')"
                                class="status-filter"
                                :hidden-items="['Device']"
                                :clearable="filters.filters.roles && filters.filters.roles !== null"
                                @change="(val) => (filters.roles = val ? val : null)"
                            ></b-input-roles>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-select
                                v-model="filters.filters.status"
                                :items="filtersOptions.status"
                                :label="t('users::label.status')"
                                :clearable="filters.filters.status !== null"
                                class="status-filter"
                                append-icon="mdi-chevron-down"
                                dense
                                outlined
                            >
                            </b-filter-select>
                        </v-col>
                    </template>

                    <template #[`item.actions`]="{ item }">
                        <div class="table-actions">
                            <v-tooltip v-if="can('users.export-record-as-pdf')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="exportRecordAsPdf(item)"
                                    >
                                        <v-icon size="24">mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.export_record_as_pdf') }}</span>
                            </v-tooltip>

                            <v-tooltip v-if="can('api.v1.users.update')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(editItemUrl(item))"
                                    >
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('users::button.edit') }}</span>
                            </v-tooltip>
                            <v-tooltip
                                v-if="can('auth.impersonate') && item.status === 'active'"
                                top
                                transition="v-fab-transition"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(impersonateUrl(item))"
                                    >
                                        <v-icon>mdi-account</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('users::button.impersonate') }}</span>
                            </v-tooltip>
                            <b-delete
                                v-if="can('api.v1.users.destroy')"
                                :path="deleteItemUrl(item)"
                                :text="t('default::confirmation.deletion')"
                                :title="t('default::confirmation.general')"
                                icon="mdi-trash-can"
                                @deleted="triggerDeleted"
                            >
                            </b-delete>
                        </div>
                    </template>
                    <template #[`item.status`]="{ item }">
                        <div class="grid-status">
                            <span>
                                {{ item.status }}
                            </span>
                        </div>
                    </template>
                    <template #[`item.created_at`]="{ item }">
                        {{ moment(item.created_at).format('DD.MM.YYYY') }}
                    </template>
                </b-datatable>
            </div>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: ['id'],
            initialFilters: { type: 'user' },
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('users::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('users::grid.label.email'),
                    value: 'email',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('users::grid.label.role'),
                    value: 'role',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('users::grid.label.status'),
                    value: 'status',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('users::grid.label.created_at'),
                    value: 'created_at',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    align: 'end',
                    width: 160,
                },
            ],
            filtersOptions: {
                status: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('users::options.status.active'),
                        value: 'active',
                    },
                    {
                        text: t('users::options.status.blocked'),
                        value: 'blocked',
                    },
                ],
            },
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('users.export-record-as-pdf', { user: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        itemUrl(item) {
            return route('users.get', { user: item.id });
        },
        editItemUrl(item) {
            return route('users.get', { user: item.id });
        },
        impersonateUrl(item) {
            return route('auth.impersonate', { id: item.id });
        },
        createItemUrl() {
            return route('users.create');
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
        deleteItemUrl(item) {
            return route('api.v1.users.destroy', { user: item.id });
        },
    },
};
</script>

<style scoped></style>
